/**
 * PrestitiPage — decisioni prestito di fine stagione.
 *
 * Mostra le decisioni pendenti che l'utente può risolvere:
 * - DDR (diritto riscatto): il presidente sceglie Riscatta / Restituisci
 * - ODR condizionato: l'admin verifica se la condizione è soddisfatta
 *
 * Secco e ODR pieno NON appaiono qui: sono già applicati automaticamente
 * alla chiusura stagione.
 */

import {
  type DecisionePrestito,
  useDecisioniPrestitoPendenti,
  useRisolviDDR,
  useVerificaODR,
} from "../../hooks/usePrestiti";
import { MobileShell } from "../shell/MobileShell";
import { tr } from "../../i18n";

function DDRCard({ d }: { d: DecisionePrestito }) {
  const risolvi = useRisolviDDR();
  const importo = d.importo_riscatto_milioni ? `${Number(d.importo_riscatto_milioni)}M` : "—";
  return (
    <div className="card space-y-3 border border-info/30">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="font-display text-lg text-accent">🤔 {d.giocatore_nome}</div>
          <div className="text-xs text-fg-muted">
            Diritto di riscatto · prestito da <strong>{d.squadra_proprietaria_nome ?? "—"}</strong>
          </div>
        </div>
        <span className="pill bg-info/20 text-info text-[10px]">DDR</span>
      </div>
      <div className="text-sm text-fg">
        {tr("Riscattalo per")} <strong className="text-accent">{importo}</strong> e diventa di tua
        proprietà, oppure restituiscilo a {d.squadra_proprietaria_nome ?? "—"}.
      </div>
      <div className="flex gap-2 flex-wrap">
        <button
          disabled={risolvi.isPending}
          onClick={() => risolvi.mutate({ decisioneId: d.id, esito: "riscattato" })}
          className="btn-primary disabled:opacity-50"
        >
          💰 Riscatta ({importo})
        </button>
        <button
          disabled={risolvi.isPending}
          onClick={() => risolvi.mutate({ decisioneId: d.id, esito: "restituito" })}
          className="btn-ghost border border-danger/40 text-danger disabled:opacity-50"
        >
          {tr("🔄 Restituisci")}
        </button>
      </div>
      {risolvi.isError && (
        <div className="text-xs text-danger">
          {(risolvi.error as { response?: { data?: { detail?: string } } }).response?.data
            ?.detail ?? tr("Errore")}
        </div>
      )}
    </div>
  );
}

function ODRCondCard({ d }: { d: DecisionePrestito }) {
  const verifica = useVerificaODR();
  const importo = d.importo_riscatto_milioni ? `${Number(d.importo_riscatto_milioni)}M` : "—";
  return (
    <div className="card space-y-3 border border-warning/30">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="font-display text-lg text-accent">⚠️ {d.giocatore_nome}</div>
          <div className="text-xs text-fg-muted">
            ODR condizionato · {d.squadra_prestito_nome} ← da {d.squadra_proprietaria_nome ?? "—"}
          </div>
        </div>
        <span className="pill bg-warning/20 text-warning text-[10px]">{tr("ODR cond.")}</span>
      </div>
      {d.condizione_riscatto_testo && (
        <div className="text-sm bg-primary-deep/40 rounded p-2 text-fg">
          <span className="text-xs text-fg-muted uppercase tracking-wider">
            {tr("Condizione:")}
          </span>
          <br />
          {d.condizione_riscatto_testo}
        </div>
      )}
      <div className="text-sm text-fg">
        La condizione è soddisfatta? Se sì → <strong>{d.squadra_prestito_nome}</strong> riscatta per{" "}
        <strong className="text-accent">{importo}</strong>. Se no → torna a{" "}
        {d.squadra_proprietaria_nome ?? "—"}.
      </div>
      <div className="flex gap-2 flex-wrap">
        <button
          disabled={verifica.isPending}
          onClick={() => verifica.mutate({ decisioneId: d.id, verificata: true })}
          className="btn-primary disabled:opacity-50"
        >
          {tr("✅ Condizione soddisfatta → riscatto")}
        </button>
        <button
          disabled={verifica.isPending}
          onClick={() => verifica.mutate({ decisioneId: d.id, verificata: false })}
          className="btn-ghost border border-danger/40 text-danger disabled:opacity-50"
        >
          {tr("❌ Non soddisfatta → rientro")}
        </button>
      </div>
      {verifica.isError && (
        <div className="text-xs text-danger">
          {(verifica.error as { response?: { data?: { detail?: string } } }).response?.data
            ?.detail ?? tr("Errore")}
        </div>
      )}
    </div>
  );
}

export function PrestitiPage() {
  const { data: decisioni, isLoading } = useDecisioniPrestitoPendenti();

  return (
    <MobileShell logoSuffix="PRESTITI">
      <h2 className="font-display text-3xl text-accent tracking-wide">
        {tr("Prestiti — fine stagione")}
      </h2>

      <div className="card text-sm text-fg-muted">
        {tr(
          tr(
            "Qui risolvi i prestiti rimasti aperti a fine stagione. I prestiti secchi (rientro) e gli ODR pieni (riscatto obbligatorio) sono già stati applicati automaticamente.",
          ),
        )}
      </div>

      {isLoading && <div className="card text-fg-muted">{tr("Caricamento…")}</div>}

      {!isLoading && (decisioni?.length ?? 0) === 0 && (
        <div className="card text-center text-fg-muted p-8">
          <div className="text-4xl mb-2">✅</div>
          <div>{tr("Nessun prestito da risolvere.")}</div>
        </div>
      )}

      <div className="space-y-3">
        {decisioni?.map((d) =>
          d.tipo_decisione === "scelta_ddr" ? (
            <DDRCard key={d.id} d={d} />
          ) : (
            <ODRCondCard key={d.id} d={d} />
          ),
        )}
      </div>
    </MobileShell>
  );
}
